import { useRef } from 'react'
import fastDeepEqual from 'fast-deep-equal'
import { AnyMemoized } from './dependencies'
import * as Sentry from '@sentry/browser'
import { normalizeValue } from 'src/normalized-data/normalization'

type ReturnType<T extends AnyMemoized[] | undefined> = T extends undefined
  ? AnyMemoized[] | undefined
  : AnyMemoized[]

const REPORTING_THRESHOLD = 3

const MAX_EXTRA_LENGTH = (200 * 1000) / 2 / 4

const stringify = (deps: AnyMemoized[] | undefined) =>
  deps !== undefined
    ? JSON.stringify(normalizeValue(deps), undefined, 2).slice(
        0,
        MAX_EXTRA_LENGTH,
      )
    : undefined

const captureSentryMessage = (
  originalDeps: AnyMemoized[] | undefined,
  newDeps: AnyMemoized[] | undefined,
) =>
  Sentry.withScope(scope => {
    scope.setExtra('original_dependencies', stringify(originalDeps))
    scope.setExtra('new_dependencies', stringify(newDeps))
    Sentry.captureException(
      Error(
        `Deep comparing dependencies took longer than ${REPORTING_THRESHOLD} milliseconds.`,
      ),
    )
  })

/**
 * Converts deep dependencies to shallow dependencies.
 */
const useMemoizedDeps = <T extends AnyMemoized[] | undefined>(
  newDeps: T,
): ReturnType<T> => {
  const mutableRef = useRef<AnyMemoized[]>()

  const start = new Date()
  const originalDeps = mutableRef.current

  if (!fastDeepEqual(originalDeps, newDeps)) {
    mutableRef.current = newDeps
  }

  const duration = new Date().getTime() - start.getTime()

  if (duration > REPORTING_THRESHOLD) {
    captureSentryMessage(originalDeps, newDeps)
  }

  if (process.env.NODE_ENV === 'development') {
    const changedDeps = newDeps?.reduce((accum, dependency, index) => {
      if (originalDeps && dependency !== originalDeps[index]) {
        return {
          ...accum,
          [index]: {
            before: originalDeps[index],
            after: dependency,
          },
        }
      }

      return accum
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    }, {} as Record<string, any>)

    if (changedDeps && Object.keys(changedDeps).length) {
      console.log('[use-effect-debugger] ', changedDeps)
    }
  }

  return mutableRef.current as ReturnType<T>
}

export default useMemoizedDeps
